import Image from "next/image";
import Link from "next/link";
import { Building2, CalendarDays, FileText, MapPin } from "lucide-react";
import { formatPriceShort, sanePrice, statusMeta } from "@/lib/v2/format";
import { Pill } from "./Pill";

export type PropertyCardV2Props = {
  slug: string;
  name: string;
  district: string;
  districtName?: string;
  address?: string;
  image: string | null;
  constructionStatus: string;
  isPreLaunch?: boolean;
  priceFrom?: number | null;
  psfFrom?: number | null;
  unitsTotal?: number | null;
  unitsLeft?: number | null;
  expectedTop?: string | null;
  tenure: string;
  href: string;
  distance?: number | string | null;
};

export function PropertyCardV2({
  name,
  district,
  districtName,
  address,
  image,
  constructionStatus,
  isPreLaunch,
  priceFrom,
  unitsTotal,
  unitsLeft,
  expectedTop,
  tenure,
  href,
  distance,
}: PropertyCardV2Props) {
  const status = statusMeta(constructionStatus, isPreLaunch);
  const location = districtName ? `${district} · ${districtName}` : address || district;
  const price = sanePrice(priceFrom);
  const shortTenure = tenure.replace("-year leasehold", "-yr").replace("999-year", "999-yr");
  const facts: Array<{ label: string; Icon: typeof Building2 }> = [];
  if (unitsTotal != null && unitsTotal > 0) facts.push({ label: `${unitsTotal} units`, Icon: Building2 });
  if (expectedTop) facts.push({ label: `TOP ${expectedTop}`, Icon: CalendarDays });
  if (tenure) facts.push({ label: shortTenure, Icon: FileText });

  return (
    <Link
      href={href}
      className="group block rounded-2xl border border-line bg-white p-3 text-ink transition-shadow hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-surface">
        {image ? (
          <Image
            src={image}
            alt={name}
            fill
            unoptimized
            className="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            sizes="(min-width: 1024px) 30vw, (min-width: 640px) 50vw, 100vw"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-ink-faint">
            <Building2 aria-hidden="true" className="size-10" />
          </div>
        )}
        <Pill tone={status.tone} className="absolute left-3 top-3">
          {status.label}
        </Pill>
        {unitsLeft != null && unitsLeft > 0 && (
          <Pill tone="avail" className="absolute right-3 top-3">
            {unitsLeft} left
          </Pill>
        )}
      </div>

      <div className="px-1 pb-1 pt-4">
        <div className="flex min-w-0 items-center gap-1.5 text-xs text-ink-muted">
          <MapPin aria-hidden="true" className="size-3.5 shrink-0" />
          <span className="truncate">{location}</span>
        </div>
        {distance != null && distance !== "" && (
          <p className="mt-1 text-xs text-ink-faint">
            {typeof distance === "number" ? `${distance.toFixed(2)} km away` : String(distance)}
          </p>
        )}
        <div className="mt-3 flex items-start justify-between gap-4">
          <h3 className="line-clamp-2 min-w-0 font-display font-semibold leading-snug text-ink">
            {name}
          </h3>
          <p className={`shrink-0 text-right text-sm font-semibold ${price != null ? "text-gold-600" : "text-ink-faint"}`}>
            {price != null ? `From ${formatPriceShort(price)}` : "Price on request"}
          </p>
        </div>

        {facts.length > 0 && (
          <ul className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-3 text-xs text-ink-muted">
            {facts.map((f, i) => (
              <li key={f.label} className="flex items-center gap-1.5">
                {i > 0 && <span aria-hidden="true" className="mr-1.5 text-ink-faint">·</span>}
                <f.Icon aria-hidden="true" className="size-3.5 shrink-0" />
                <span>{f.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Link>
  );
}
